/**
 * appearancePredictor.js
 * 
 * Predicts physical traits from well-known SNPs
 */

const TRAIT_PREDICTORS = {
  eyeColor: {
    label: 'Eye Color',
    icon: '👁️',
    snps: {
      'rs12913832': { // HERC2/OCA2 - main eye color SNP
        'AA': { prediction: 'Brown eyes', confidence: 'very high', description: 'Almost certainly brown eyes' },
        'AG': { prediction: 'Brown or green eyes', confidence: 'high', description: 'Likely brown, possibly green/hazel' },
        'GG': { prediction: 'Blue eyes', confidence: 'very high', description: 'Almost certainly blue eyes' }
      },
      'rs1800407': { // OCA2 modifier
        'TT': { prediction: 'Brown eyes', confidence: 'moderate', description: 'Contributes to brown pigmentation' },
        'CT': { prediction: 'Intermediate', confidence: 'low', description: 'Lighter brown or hazel possible' },
        'CC': { prediction: 'Blue/Green eyes', confidence: 'moderate', description: 'Reduces melanin, lighter eyes' }
      }
    }
  },
  
  hairColor: {
    label: 'Hair Color',
    icon: '💇',
    snps: {
      'rs1805007': { // MC1R - red hair variant
        'CC': { prediction: 'Non-red hair', confidence: 'high', description: 'Normal MC1R function' },
        'CT': { prediction: 'Possible red tint', confidence: 'moderate', description: 'One red hair variant' },
        'TT': { prediction: 'Red hair', confidence: 'high', description: 'Two red hair variants - likely red or auburn hair' }
      },
      'rs12896399': { // SLC24A4 - blond hair
        'GG': { prediction: 'Darker hair', confidence: 'moderate', description: 'Less likely to be blond' },
        'GT': { prediction: 'Light brown', confidence: 'low', description: 'Intermediate pigmentation' },
        'TT': { prediction: 'Blond hair', confidence: 'moderate', description: 'Blond hair allele' }
      }
    }
  },
  
  hairType: {
    label: 'Hair Texture',
    icon: '🦱',
    snps: {
      'rs11803731': { // TCHH - hair curl
        'AA': { prediction: 'Straight hair', confidence: 'moderate', description: 'Straight hair more likely' },
        'AG': { prediction: 'Wavy hair', confidence: 'low', description: 'Intermediate curl' },
        'GG': { prediction: 'Curly hair', confidence: 'moderate', description: 'Curly hair more likely' }
      }
    }
  },
  
  skinTone: {
    label: 'Skin Pigmentation',
    icon: '🎨',
    snps: {
      'rs1426654': { // SLC24A5 - major skin color SNP
        'AA': { prediction: 'Lighter skin', confidence: 'very high', description: 'European light skin variant' },
        'AG': { prediction: 'Intermediate skin tone', confidence: 'high', description: 'Mixed pigmentation' },
        'GG': { prediction: 'Darker skin', confidence: 'very high', description: 'Ancestral darker skin variant' }
      },
      'rs1800414': { // OCA2/SLC45A2
        'CC': { prediction: 'Darker skin', confidence: 'moderate', description: 'Normal melanin production' },
        'CT': { prediction: 'Medium skin tone', confidence: 'low', description: 'Reduced melanin' },
        'TT': { prediction: 'Lighter skin', confidence: 'moderate', description: 'East Asian light skin variant' }
      }
    }
  },
  
  lactoseIntolerance: {
    label: 'Lactose Tolerance',
    icon: '🥛',
    snps: {
      'rs4988235': { // LCT - lactase persistence
        'AA': { prediction: 'Likely lactose intolerant', confidence: 'high', description: 'Lactase production decreases after childhood' },
        'AG': { prediction: 'Possibly lactose tolerant', confidence: 'moderate', description: 'Intermediate lactase persistence' },
        'GG': { prediction: 'Lactose tolerant', confidence: 'high', description: 'Lactase persists into adulthood (European variant)' }
      }
    }
  },
  
  bitterTaste: {
    label: 'Bitter Taste Sensitivity',
    icon: '😝',
    snps: {
      'rs713598': { // TAS2R38 - PTC taster
        'CC': { prediction: 'Cannot taste bitter (non-taster)', confidence: 'high', description: 'Non-functional bitter receptor' },
        'CG': { prediction: 'Medium bitter sensitivity', confidence: 'moderate', description: 'Moderate taster' },
        'GG': { prediction: 'Very sensitive to bitter tastes', confidence: 'high', description: 'Super-taster for PTC/PROP' }
      }
    }
  }
};

/**
 * Analyze appearance traits from SNP data
 */
export const predictAppearanceTraits = (snps) => {
  const predictions = {};
  
  // Create a map of rsid -> SNP for quick lookup
  const snpMap = new Map();
  snps.forEach(snp => snpMap.set(snp.rsid, snp));
  
  // For each trait category
  for (const [traitKey, traitData] of Object.entries(TRAIT_PREDICTORS)) {
    const traitPredictions = [];
    
    // Check each SNP for this trait
    for (const [rsid, genotypes] of Object.entries(traitData.snps)) {
      const userSnp = snpMap.get(rsid);
      
      if (userSnp) {
        const genotype = userSnp.genotype;
        const prediction = genotypes[genotype];
        
        if (prediction) {
          traitPredictions.push({
            rsid,
            genotype,
            ...prediction,
            gene: userSnp.gene
          });
        }
      }
    }
    
    if (traitPredictions.length > 0) {
      predictions[traitKey] = {
        ...traitData,
        predictions: traitPredictions,
        summary: generateTraitSummary(traitKey, traitPredictions)
      };
    }
  }
  
  return predictions;
};

/**
 * Generate a summary for a trait based on all SNPs
 */
const generateTraitSummary = (traitKey, predictions) => {
  if (predictions.length === 0) return null;
  
  // For traits with multiple SNPs, combine predictions
  if (traitKey === 'eyeColor') {
    const mainSnp = predictions.find(p => p.rsid === 'rs12913832');
    return mainSnp ? mainSnp.prediction : predictions[0].prediction;
  }
  
  if (traitKey === 'hairColor') {
    const redHair = predictions.find(p => p.rsid === 'rs1805007');
    const blondHair = predictions.find(p => p.rsid === 'rs12896399');
    
    if (redHair?.prediction.includes('Red')) {
      return redHair.prediction;
    }
    if (blondHair?.prediction.includes('Blond')) {
      return blondHair.prediction;
    }
    return predictions[0].prediction;
  }
  
  // For single-SNP traits, just use the primary prediction
  return predictions[0].prediction;
};

/**
 * Generate a natural language summary of all appearance predictions
 */
export const generateAppearanceSummary = (predictions) => {
  if (Object.keys(predictions).length === 0) {
    return "I couldn't find enough appearance-related SNPs in your data to make predictions.";
  }
  
  const parts = [];
  
  if (predictions.eyeColor) {
    const summary = predictions.eyeColor.summary;
    const confidence = predictions.eyeColor.predictions[0]?.confidence || 'moderate';
    parts.push(`**Eye Color:** ${summary} (${confidence} confidence)`);
  }
  
  if (predictions.hairColor) {
    const summary = predictions.hairColor.summary;
    parts.push(`**Hair Color:** ${summary}`);
  }
  
  if (predictions.hairType) {
    const summary = predictions.hairType.summary;
    parts.push(`**Hair Texture:** ${summary}`);
  }
  
  if (predictions.skinTone) {
    const summary = predictions.skinTone.summary;
    parts.push(`**Skin Tone:** ${summary}`);
  }
  
  if (predictions.lactoseIntolerance) {
    const summary = predictions.lactoseIntolerance.summary;
    parts.push(`**Lactose:** ${summary}`);
  }
  
  if (predictions.bitterTaste) {
    const summary = predictions.bitterTaste.summary;
    parts.push(`**Taste:** ${summary}`);
  }
  
  return parts.join('\n');
};

/**
 * Get detailed explanation for a specific trait
 */
export const getTraitDetails = (predictions, traitKey) => {
  const trait = predictions[traitKey];
  if (!trait) return null;
  
  const details = [`## ${trait.label} ${trait.icon}\n`];
  
  trait.predictions.forEach(pred => {
    details.push(`**${pred.rsid}** (${pred.gene}): ${pred.genotype}`);
    details.push(`→ ${pred.description}`);
    details.push('');
  });
  
  details.push(`**Overall prediction:** ${trait.summary}`);
  
  return details.join('\n');
};

export default {
  predictAppearanceTraits,
  generateAppearanceSummary,
  getTraitDetails,
  TRAIT_PREDICTORS
};